import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import Cookies from "js-cookie";
import { RootState } from "../../app/store";

export const api = createApi({
    baseQuery: fetchBaseQuery({
        baseUrl: process.env.REACT_APP_API_URL,
        credentials: "include",
        prepareHeaders: (headers, { getState }) => {
            const xsrfToken = Cookies.get("XSRF-TOKEN");
            const token = (getState() as RootState).windowSlice.token;
            if (xsrfToken) {
                headers.set("X-XSRF-TOKEN", xsrfToken);
            }
            if (token) {
                headers.set("Authorization", `Bearer ${token}`);
            }
            return headers;
        },
    }),

    endpoints: (builder) => ({
        register: builder.mutation<any, { data: string }>({
            query: (data) => ({
                url: `/${process.env.REACT_APP_API_VERSION}/register`,
                method: "POST",
                body: {
                    data: data.data,
                },
            }),
        }),

        login: builder.mutation<any, { telegram_id: number; password: string }>(
            {
                query: (data) => ({
                    url: `/${process.env.REACT_APP_API_VERSION}/login`,
                    method: "POST",
                    body: {
                        telegram_id: data.telegram_id,
                        password: data.password,
                    },
                }),
            }
        ),

        getBepaidToken: builder.mutation<
            any,
            { project_id: string; provider_id: string }
        >({
            query: (data) => ({
                url: `/${process.env.REACT_APP_API_VERSION}/account/bind_card`,
                method: "PUT",
                body: {
                    provider_id: data.provider_id,
                },
            }),
        }),

        getPaymentMethods: builder.query<any, { project_id: string }>({
            query: (data) => ({
                url: `/${process.env.REACT_APP_API_VERSION}/payment_methods`,
                method: "GET",
            }),
        }),

        getSavedCards: builder.query<any, { project_id: string }>({
            query: (data) => ({
                url: `/${process.env.REACT_APP_API_VERSION}/account/saved_cards`,
                method: "GET",
            }),
        }),

        deleteSavedCard: builder.mutation<
            any,
            { project_id: string; card_id: string }
        >({
            query: (data) => ({
                url: `/${process.env.REACT_APP_API_VERSION}/project/${data.project_id}/account/card/${data.card_id}`,
                method: "DELETE",
            }),
        }),

        getMyProjects: builder.query<any, { account_id: string }>({
            query: (data) => ({
                url: `/${process.env.REACT_APP_API_VERSION}/account/${data.account_id}/projects`,
                method: "GET",
            }),
        }),

        getStatistics: builder.query<any, { project_id: string }>({
            query: (data) => ({
                url: `/${process.env.REACT_APP_API_VERSION}/admin/project/${data.project_id}/statistics`,
                method: "GET",
            }),
        }),

        getStatisticsCategory: builder.mutation<
            any,
            { project_id: string; password: string; type: string }
        >({
            query: (data) => ({
                url: `/${process.env.REACT_APP_API_VERSION}/admin/project/${data.project_id}/statistics/${data.type}`,
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: {
                    password: data.password,
                },
            }),
        }),

        getProjectGroupsAndPlans: builder.query<any, { project_id: string }>({
            query: (data) => ({
                url: `/${process.env.REACT_APP_API_VERSION}/admin/project/${data.project_id}/groups`,
                method: "GET",
            }),
        }),

        getProjectTariff: builder.query<any, { project_id: string }>({
            query: (data) => ({
                url: `/${process.env.REACT_APP_API_VERSION}/admin/project/${data.project_id}/tariff`,
                method: "GET",
            }),
        }),

        getProducts: builder.query<any, any>({
            query: () => ({
                url: `/${process.env.REACT_APP_API_VERSION}/product`,
                method: "GET",
            }),
        }),

        refundPayment: builder.mutation<
            any,
            { project_id: string; payment_id: string; with_cancel: boolean }
        >({
            query: (data) => ({
                url: `/${process.env.REACT_APP_API_VERSION}/admin/project/${data.project_id}/payment/${data.payment_id}/refund`,
                method: "POST",
                body: {
                    with_cancel: data.with_cancel,
                },
            }),
        }),

        getStatisticByPlan: builder.mutation<
            any,
            {
                project_id: string;
                payment_types: string[];
                start_date: string;
                end_date: string;
                plans_ids: (string | number)[];
            }
        >({
            query: (data) => ({
                url: `/${process.env.REACT_APP_API_VERSION}/admin/project/${data.project_id}/statistics/plan`,
                method: "POST",
                body: {
                    payment_types: data.payment_types,
                    start_date: data.start_date,
                    end_date: data.end_date,
                    plans_ids: data.plans_ids,
                },
            }),
        }),

        tariffAddProducts: builder.mutation<
            any,
            {
                project_id: string;
                products: {
                    product_item_id: string;
                    count: number;
                }[];
            }
        >({
            query: (data) => ({
                url: `/${process.env.REACT_APP_API_VERSION}/admin/project/${data.project_id}/tariff/add_products`,
                method: "POST",
                body: data.products,
            }),
        }),

        tariffAddProductsCharge: builder.mutation<
            any,
            {
                project_id: string;
            }
        >({
            query: (data) => ({
                url: `/${process.env.REACT_APP_API_VERSION}/admin/project/${data.project_id}/tariff/charge_added`,
                method: "POST",
            }),
        }),

        prognosisPayments: builder.mutation<
            any,
            {
                project_id: string;
                start_date: string;
                end_date: string;
            }
        >({
            query: (data) => ({
                url: `/${process.env.REACT_APP_API_VERSION}/admin/project/${data.project_id}/statistics/subscriptions_count_by_date`,
                method: "POST",
                body: {
                    start_date: data.start_date,
                    end_date: data.end_date,
                },
            }),
        }),

        getCsrf: builder.query<any, any>({
            query: () => ({
                url: `/sanctum/csrf-cookie`,
                method: "GET",
            }),
        }),

        //--------------------------------------------------------------------------------

        getMySubscriptions: builder.query<any, { project_id: string }>({
            query: (data) => ({
                url: `/project/${data.project_id}/subscriptions`,
                method: "GET",
            }),
        }),

        getAvailablePlans: builder.query<any, { project_id: string }>({
            query: (data) => ({
                url: `/project/${data.project_id}/plans`,
                method: "GET",
            }),
        }),

        getPlanByLink: builder.query<
            any,
            { project_id: string; hash_start: string }
        >({
            query: (data) => ({
                url: `/project/${data.project_id}/plan/${data.hash_start}`,
                method: "GET",
            }),
        }),

        subscribe: builder.mutation<
            any,
            { project_id: string; plan_id: string; is_trial?: boolean }
        >({
            query: (data) => ({
                url: `/project/${data.project_id}/plan/${data.plan_id}/subscribe`,
                method: "POST",
                body: {
                    use_trial: data.is_trial ? data.is_trial : false,
                },
            }),
        }),

        cancelSubscription: builder.mutation<
            any,
            { project_id: string; subscription_id: string; step?: number }
        >({
            query: (data) => ({
                url: `/project/${data.project_id}/subscription/${data.subscription_id}/cancel`,
                method: "POST",
                body: {
                    step: data.step ? data.step : 0,
                },
            }),
        }),

        renewSubscription: builder.mutation<
            any,
            { project_id: string; subscription_id: string }
        >({
            query: (data) => ({
                url: `/project/${data.project_id}/subscription/${data.subscription_id}/renew`,
                method: "POST",
            }),
        }),

        acceptProposition: builder.mutation<
            any,
            { project_id: string; subscription_id: string; step: number }
        >({
            query: (data) => ({
                url: `/project/${data.project_id}/subscription/${data.subscription_id}/accept_proposition`,
                method: "POST",
                body: {
                    step: data.step,
                },
            }),
        }),
    }),
});

export const {
    useRegisterMutation,
    useGetBepaidTokenMutation,
    useLoginMutation,
    useGetPaymentMethodsQuery,
    useGetSavedCardsQuery,
    useDeleteSavedCardMutation,
    useGetMyProjectsQuery,
    useGetStatisticsQuery,
    useGetStatisticsCategoryMutation,
    useGetProjectGroupsAndPlansQuery,
    useGetProjectTariffQuery,
    useGetProductsQuery,
    useRefundPaymentMutation,
    useGetStatisticByPlanMutation,
    useTariffAddProductsMutation,
    useTariffAddProductsChargeMutation,
    usePrognosisPaymentsMutation,
    useGetCsrfQuery,

    useGetMySubscriptionsQuery,
    useGetAvailablePlansQuery,
    useSubscribeMutation,
    useCancelSubscriptionMutation,
    useRenewSubscriptionMutation,
    useAcceptPropositionMutation,
    useGetPlanByLinkQuery,
} = api;
